import type { Cart, ShippingMethod } from "@commercetools/platform-sdk";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useEffect, useMemo, useState } from "react";
import { formatPrice } from "../../utils/cart/currency";
import {
	amountRemainingUntilFreeShipping,
	getLowestFreeShippingAboveAmount,
} from "../../utils/cart/shipping";
import { getShippingMethodsForCart } from "../../utils/commercetools/shipping-methods-service";

export interface FreeShipping {
	freeShippingUSP: string;
	lowestFreeAboveUSP: string;
}

export const useFreeShipping = (cart?: Cart | null): FreeShipping => {
	const [loadPending, setLoadPending] = useState(false);
	const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>();
	const { t } = useTranslation();

	useEffect(() => {
		const getAndSetShippingMethods = async (cart: Cart) => {
			setLoadPending(true);
			const shippingMethods = await getShippingMethodsForCart(cart);
			setLoadPending(false);
			setShippingMethods(shippingMethods);
		};

		if (cart && !loadPending && !shippingMethods) {
			void getAndSetShippingMethods(cart);
		}
	}, [cart, loadPending, shippingMethods]);

	const freeShippingUSPs = useMemo<FreeShipping>(() => {
		const store = cart?.store?.key ?? "";
		if (shippingMethods) {
			let lowestFreeAbove = getLowestFreeShippingAboveAmount(shippingMethods);

			//TODO: Hotfixed, changed lowestFreeAbove for NAM to 0 as it was not working as intended with freeShipping set to 0.
			switch (store) {
				case "us": {
					lowestFreeAbove = 50;
					break;
				}
				case "ca": {
					lowestFreeAbove = 65;
					break;
				}
				case "au": {
					lowestFreeAbove = 100;
					break;
				}
				default: {
					break;
				}
			}

			if (lowestFreeAbove !== undefined) {
				const remainingUntilFreeShipping = amountRemainingUntilFreeShipping(
					lowestFreeAbove,
					cart,
				);
				const usp =
					cart?.totalPrice.fractionDigits && remainingUntilFreeShipping > 0
						? formatPrice(
								cart.totalPrice.currencyCode,
								remainingUntilFreeShipping,
							) +
							" " +
							t("left to free shipping")
						: t("Free shipping!");

				const lowest =
					cart?.totalPrice.fractionDigits && lowestFreeAbove
						? t("Free shipping from") +
							" " +
							formatPrice(cart.totalPrice.currencyCode, lowestFreeAbove)
						: "";

				return { freeShippingUSP: usp, lowestFreeAboveUSP: lowest };
			}
		}
		return { freeShippingUSP: "", lowestFreeAboveUSP: "" };
	}, [cart, shippingMethods, t]);

	return freeShippingUSPs;
};
